/**
 * Setup-scherm: teams, per-set beginsituatie, format/drempels, profiel,
 * audiotest en toegang tot camera/kalibratie.
 */

import { h } from "./dom";
import type { App, SetPlan } from "./app";
import { openSetConfirm } from "./setConfirm";

export function renderSetup(app: App, root: HTMLElement): void {
  const cfg = app.config;
  const teams = app.resolveConfig().teams;

  // zet app.config.match.teams zodanig dat het formulier de bron van waarheid is
  cfg.match.teams = { A: structuredClone(teams.A), B: structuredClone(teams.B) };

  const teamA = h("fieldset", { class: "card" }, [h("legend", {}, ["Team A"])]);
  const teamB = h("fieldset", { class: "card" }, [h("legend", {}, ["Team B"])]);

  function teamFields(set: HTMLElement, key: "A" | "B"): void {
    const name = h("input", { type: "text", value: teams[key].name, id: `name-${key}` });
    const p1 = h("input", { type: "text", value: teams[key].players[0], id: `p1-${key}` });
    const p2 = h("input", { type: "text", value: teams[key].players[1], id: `p2-${key}` });
    set.append(
      h("label", { class: "field" }, ["Teamnaam", name]),
      h("label", { class: "field" }, ["Speler 1 (eerste serveerder)", p1]),
      h("label", { class: "field" }, ["Speler 2", p2]),
    );
  }
  teamFields(teamA, "A");
  teamFields(teamB, "B");

  const setCards: HTMLElement[] = [1, 2, 3].map((n) => {
    const plan: SetPlan = structuredClone(cfg.setPlans[n - 1] ?? defaultSetPlan(n));
    const firstServe = h("select", {}, [
      h("option", { value: "A", selected: plan.firstServeTeam === "A" }, [teams.A.name]),
      h("option", { value: "B", selected: plan.firstServeTeam === "B" }, [teams.B.name]),
    ]);
    const s1 = h("select", {}, [
      h("option", { value: "0", selected: plan.firstInOrder.A === 0 }, [teams.A.players[0]]),
      h("option", { value: "1", selected: plan.firstInOrder.A === 1 }, [teams.A.players[1]]),
    ]);
    const s2 = h("select", {}, [
      h("option", { value: "0", selected: plan.firstInOrder.B === 0 }, [teams.B.players[0]]),
      h("option", { value: "1", selected: plan.firstInOrder.B === 1 }, [teams.B.players[1]]),
    ]);
    const sides = h("select", {}, [
      h("option", { value: "A-left", selected: plan.startSides.A === "left" }, [`${teams.A.name} links`]),
      h("option", { value: "A-right", selected: plan.startSides.A === "right" }, [`${teams.A.name} rechts`]),
    ]);
    s1.addEventListener("change", () => (plan.firstInOrder.A = Number(s1.value) as 0 | 1));
    s2.addEventListener("change", () => (plan.firstInOrder.B = Number(s2.value) as 0 | 1));
    sides.addEventListener("change", () => {
      const aLeft = sides.value === "A-left";
      plan.startSides = { A: aLeft ? "left" : "right", B: aLeft ? "right" : "left" };
    });
    firstServe.addEventListener("change", () => (plan.firstServeTeam = firstServe.value as "A" | "B"));
    const card = h("fieldset", { class: "card half" }, [
      h("legend", {}, [`Set ${n} — beginsituatie`]),
      h("label", { class: "field" }, ["Serveert als eerste", firstServe]),
      ...(n > 1
        ? [h("p", { class: "muted small" }, ["Deze wordt bij de start van de set nogmaals bevestigd (geen automatische aannames)."])]
        : []),
      h("label", { class: "field" }, [`Eerste serveerder — ${teams.A.name}`, s1]),
      h("label", { class: "field" }, [`Eerste serveerder — ${teams.B.name}`, s2]),
      h("label", { class: "field" }, ["Startzijde aan de camera", sides]),
    ]);
    (card as HTMLElement & { __plan?: SetPlan }).__plan = plan;
    return card;
  });

  // Formaat / drempels (standaardwaarden vooraf ingevuld)
  const conf = app.resolveConfig();
  const fBestOf = h("select", {}, [
    h("option", { value: "1", selected: cfg.match.bestOf === 1 }, ["Best of 1"]),
    h("option", { value: "3", selected: (cfg.match.bestOf ?? 3) === 3 }, ["Best of 3 (standaard)"]),
  ]);
  const fT1 = h("input", { type: "number", value: String(conf.setTargets[0]), min: "1" });
  const fT2 = h("input", { type: "number", value: String(conf.setTargets[1]), min: "1" });
  const fT3 = h("input", { type: "number", value: String(conf.setTargets[2]), min: "1" });
  const fLead = h("input", { type: "number", value: String(conf.minLead), min: "1" });
  const fEveryN = h("input", { type: "number", value: String(conf.sideSwitchEvery.normal), min: "1" });
  const fEvery3 = h("input", { type: "number", value: String(conf.sideSwitchEvery.thirdSet), min: "1" });
  const fHigh = h("input", { type: "number", step: "0.01", min: "0", max: "1", value: String(conf.highConfidence) });
  const fMed = h("input", { type: "number", step: "0.01", min: "0", max: "1", value: String(conf.mediumConfidence) });
  const fAuth = h("input", { type: "number", step: "0.01", min: "0", max: "1", value: String(conf.authoritativeServeConfidence) });
  const fConfSrv = h("input", { type: "number", step: "0.01", min: "0", max: "1", value: String(conf.confirmServeConfidence) });

  const fProfile = h("select", {}, [
    h("option", { value: "low", selected: cfg.perfProfile === "low" }, ["Laag (320p / 10 fps, oud toestel)"]),
    h("option", { value: "mid", selected: cfg.perfProfile === "mid" }, ["Midden (640p / 15 fps, standaard)"]),
    h("option", { value: "high", selected: cfg.perfProfile === "high" }, ["Hoog (960p / 30 fps, moderne telefoon)"]),
  ]);

  const status = h("p", { id: "setup-ok", class: "muted small" });
  function collect(): boolean {
    try {
      teams.A.name = (app.root.querySelector("#name-A") as HTMLInputElement).value.trim() || "Team A";
      teams.A.players = [
        (app.root.querySelector("#p1-A") as HTMLInputElement).value.trim() || "Speler 1",
        (app.root.querySelector("#p2-A") as HTMLInputElement).value.trim() || "Speler 2",
      ];
      teams.B.name = (app.root.querySelector("#name-B") as HTMLInputElement).value.trim() || "Team B";
      teams.B.players = [
        (app.root.querySelector("#p1-B") as HTMLInputElement).value.trim() || "Speler 1",
        (app.root.querySelector("#p2-B") as HTMLInputElement).value.trim() || "Speler 2",
      ];
    } catch {
      /* formulier nog niet volledig? */
    }
    cfg.match.teams = { A: structuredClone(teams.A), B: structuredClone(teams.B) };
    cfg.match.bestOf = Number(fBestOf.value);
    cfg.match.setTargets = [Number(fT1.value), Number(fT2.value), Number(fT3.value)];
    cfg.match.minLead = Number(fLead.value);
    cfg.match.sideSwitchEvery = { normal: Number(fEveryN.value), thirdSet: Number(fEvery3.value) };
    cfg.match.highConfidence = Number(fHigh.value);
    cfg.match.mediumConfidence = Number(fMed.value);
    cfg.match.authoritativeServeConfidence = Number(fAuth.value);
    cfg.match.confirmServeConfidence = Number(fConfSrv.value);
    cfg.perfProfile = fProfile.value as "low" | "mid" | "high";
    setCards.forEach((c, i) => (cfg.setPlans[i] = structuredClone((c as HTMLElement & { __plan: SetPlan }).__plan)));
    return true;
  }

  const form = h("div", { class: "stack" }, [
    h("h1", { class: "title" }, ["🏐 Beachscorebord v2"]),
    h("p", { class: "muted" }, [
      "Vision-ondersteunde rallyscoring: de camera is de primaire scorebron; de volgende opslag is een onafhankelijke controle, niet de scoremethode.",
    ]),
    h("div", { class: "grid2" }, [teamA, teamB]),
    h("div", { class: "grid3" }, setCards),
    h("fieldset", { class: "card" }, [
      h("legend", {}, ["Format en drempels (officiële waarden vooraf ingevuld)"]),
      h("div", { class: "grid2" }, [
        h("label", { class: "field" }, ["Speelvorm", fBestOf]),
        h("label", { class: "field" }, ["Puntengrens set 1", fT1]),
        h("label", { class: "field" }, ["Puntengrens set 2", fT2]),
        h("label", { class: "field" }, ["Puntengrens set 3 (beslissend)", fT3]),
        h("label", { class: "field" }, ["Minimaal puntenverschil", fLead]),
        h("label", { class: "field" }, ["Kantwissel-veelvoud set 1/2", fEveryN]),
        h("label", { class: "field" }, ["Kantwissel-veelvoud set 3", fEvery3]),
        h("label", { class: "field" }, ["High confidence (direct punt)", fHigh]),
        h("label", { class: "field" }, ["Medium confidence (voorstel)", fMed]),
        h("label", { class: "field" }, ["Opslag autoritatief (correctie/fallback)", fAuth]),
        h("label", { class: "field" }, ["Opslag bevestigend (minimum)", fConfSrv]),
        h("label", { class: "field" }, ["Doelapparaat / performanceprofiel", fProfile]),
      ]),
    ]),
    h("div", { class: "btn-row" }, [
      h("button", { class: "btn primary big", onclick: () => { collect(); app.speech.unlock(); app.speech.say("Twee–nul. Punt Team A. Eén serveert."); status.textContent = "Audio ontgrendeld en stem geladen. Klik nogmaals om de test te horen."; } }, ["🔊 Audiotest"]),
      h("button", { class: "btn", onclick: () => { collect(); app.go("camera"); } }, ["📷 Camera & kalibratie"]),
    ]),
    h("div", { class: "btn-row" }, [
      h("button", {
        class: "btn primary big",
        onclick: () => {
          collect();
          saveAndStart(app, 1);
        },
      }, ["▶ Start wedstrijd (set 1)"]),
    ]),
    status,
  ]);
  // delegatie voor collect(): formuliervelden blijven bereikbaar via root
  root.append(form);

  function saveAndStart(a: App, setNumber: number): void {
    a.config.match.teams = { A: structuredClone(teams.A), B: structuredClone(teams.B) };
    saveConfig(a);
    // bevestiging eerst; de engine wordt pas bij bevestiging gestart
    openSetConfirm(a, setNumber, () => a.go("live"));
  }
}

function saveConfig(a: App): void {
  try {
    localStorage.setItem("bsv2:app", JSON.stringify(a.config));
  } catch {
    /* negeren */
  }
}

function defaultSetPlan(n: number): SetPlan {
  return {
    firstServeTeam: n === 2 ? "B" : "A",
    firstInOrder: { A: 0, B: 0 },
    startSides: n === 2 ? { A: "right", B: "left" } : { A: "left", B: "right" },
  };
}
